(() => {
  const WIDGET_BUTTON_ID = "accessiblyAppDialogButton";
  const WIDGET_DIALOG_ID = "accessiblyAppDialog";
  const BOGOS_CONTAINER_ID = "freegifts-main-popup-container";
  const BOGOS_VISIBLE_CLASS = "sca-is-visible-fg";
  const BOGOS_DIALOG_SELECTOR = ".bogos-gift-slider-modal";
  const BOGOS_TITLE_SELECTOR = ".bogos-slider-info-title";
  const BOGOS_CLOSE_SELECTOR = ".bogos-slider-close-btn";
  const BOGOS_TITLE_ID = "bogosGiftDialogTitle";
  const ANNOUNCEMENT_LINK_SELECTOR = ".deco-banner-link";
  const ANNOUNCEMENT_TEXT_SELECTOR = ".deco-banner-text";

  let activeModal = null;
  let bogosContainer = null;
  let bogosDialog = null;
  let widgetDialog = null;
  let bogosWasOpen = false;
  let widgetWasOpen = false;
  let lastFocusedOutsideBogos = null;
  let refreshActiveTrap = false;
  let syncScheduled = false;
  let cartDrawerTrapObserver = null;
  let cartDrawerTrapTimeout = null;

  function getWidgetButton() {
    const button = document.getElementById(WIDGET_BUTTON_ID);
    return button instanceof HTMLButtonElement ? button : null;
  }

  function makeWidgetButtonTabbable() {
    const button = getWidgetButton();
    if (button?.getAttribute("tabindex") === "-1") {
      // Native buttons already support Tab, Enter and Space.
      button.removeAttribute("tabindex");
    }
  }

  function configureAnnouncementLinks() {
    document.querySelectorAll(ANNOUNCEMENT_LINK_SELECTOR).forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      if (link.getAttribute("aria-label")?.trim()) return;

      const slide = link.closest(".deco-slick-slide") || link.parentElement;
      const label = slide
        ?.querySelector(ANNOUNCEMENT_TEXT_SELECTOR)
        ?.textContent?.replace(/\s+/g, " ")
        .trim();

      if (label) link.setAttribute("aria-label", label);
    });
  }

  function isVisible(element) {
    if (!element?.isConnected) return false;

    const style = window.getComputedStyle(element);
    return style.display !== "none" && style.visibility !== "hidden";
  }

  function isBogosOpen() {
    return Boolean(
      bogosContainer?.classList.contains(BOGOS_VISIBLE_CLASS) &&
        bogosDialog?.isConnected,
    );
  }

  function configureBogosDialog() {
    bogosContainer = document.getElementById(BOGOS_CONTAINER_ID);
    bogosDialog = bogosContainer?.querySelector(BOGOS_DIALOG_SELECTOR) || null;
    if (!bogosDialog) return;

    const title = bogosDialog.querySelector(BOGOS_TITLE_SELECTOR);
    const closeButton = bogosDialog.querySelector(BOGOS_CLOSE_SELECTOR);

    bogosDialog.setAttribute("role", "dialog");
    bogosDialog.setAttribute("aria-modal", "true");
    bogosDialog.setAttribute(
      "aria-hidden",
      bogosContainer.classList.contains(BOGOS_VISIBLE_CLASS) ? "false" : "true",
    );

    if (title) {
      title.id = BOGOS_TITLE_ID;
      bogosDialog.setAttribute("aria-labelledby", BOGOS_TITLE_ID);
      bogosDialog.removeAttribute("aria-label");
    } else {
      bogosDialog.removeAttribute("aria-labelledby");
      bogosDialog.setAttribute("aria-label", "Select your free gift");
    }

    if (closeButton instanceof HTMLButtonElement) {
      closeButton.setAttribute("aria-label", "Close free gift dialog");
      closeButton.querySelector("svg")?.setAttribute("aria-hidden", "true");
    }

    // Gift selection is already a native button; leave its app behavior untouched.
  }

  function getInitialFocus(modal) {
    if (modal.kind === "bogos") {
      return modal.dialog.querySelector(BOGOS_CLOSE_SELECTOR) || modal.dialog;
    }

    return (
      modal.dialog.querySelector("#accessiblyAppDialogCloseButton") || modal.dialog
    );
  }

  function activateModal(kind, dialog, opener) {
    if (!dialog) return;

    if (activeModal?.dialog !== dialog) {
      removeTrapFocus();
      activeModal = { kind, dialog, opener };
    }

    const currentFocus = document.activeElement;
    const focusTarget =
      currentFocus instanceof HTMLElement && dialog.contains(currentFocus)
        ? currentFocus
        : getInitialFocus(activeModal);

    trapFocus(dialog, focusTarget);
    refreshActiveTrap = false;
  }

  function stopCartDrawerTrapRefresh() {
    cartDrawerTrapObserver?.disconnect();
    cartDrawerTrapObserver = null;

    if (cartDrawerTrapTimeout) {
      window.clearTimeout(cartDrawerTrapTimeout);
      cartDrawerTrapTimeout = null;
    }
  }

  function restoreCartDrawerTrap(cartDrawer) {
    stopCartDrawerTrapRefresh();

    let refreshScheduled = false;
    const refreshTrap = () => {
      refreshScheduled = false;

      if (
        activeModal ||
        isBogosOpen() ||
        cartDrawer.getAttribute("aria-hidden") !== "false" ||
        !isVisible(cartDrawer)
      ) {
        stopCartDrawerTrapRefresh();
        return;
      }

      const currentFocus = document.activeElement;
      const focusTarget =
        currentFocus instanceof HTMLElement && cartDrawer.contains(currentFocus)
          ? currentFocus
          : cartDrawer;

      // The theme snapshots the drawer's focusable elements when trapFocus runs.
      // Refresh that snapshot while the app finishes re-rendering the cart.
      trapFocus(cartDrawer, focusTarget);
    };

    refreshTrap();

    cartDrawerTrapObserver = new MutationObserver(() => {
      if (refreshScheduled) return;
      refreshScheduled = true;
      window.requestAnimationFrame(refreshTrap);
    });
    cartDrawerTrapObserver.observe(cartDrawer, {
      childList: true,
      subtree: true,
    });

    // BOGOS/cart updates complete quickly; do not keep an observer indefinitely.
    cartDrawerTrapTimeout = window.setTimeout(stopCartDrawerTrapRefresh, 1500);
  }

  function deactivateModal() {
    if (!activeModal) return;

    const modalKind = activeModal.kind;
    const opener =
      modalKind === "widget"
        ? getWidgetButton() || activeModal.opener
        : activeModal.opener;
    activeModal = null;

    const cartDrawer = document.getElementById("cart-drawer");
    if (
      modalKind === "bogos" &&
      cartDrawer instanceof HTMLElement &&
      cartDrawer.getAttribute("aria-hidden") === "false" &&
      isVisible(cartDrawer)
    ) {
      // BOGOS can be stacked above the cart drawer. Restore the drawer's
      // existing theme focus trap instead of returning focus behind it.
      restoreCartDrawerTrap(cartDrawer);

      window.setTimeout(() => {
        if (activeModal || isBogosOpen() || !opener?.isConnected) return;
        if (cartDrawer.getAttribute("aria-hidden") === "false") return;

        const currentFocus = document.activeElement;
        if (
          currentFocus === document.body ||
          currentFocus === document.documentElement ||
          currentFocus === cartDrawer ||
          cartDrawer.contains(currentFocus)
        ) {
          removeTrapFocus(opener);
        }
      }, 800);

      return;
    }

    removeTrapFocus(opener?.isConnected ? opener : null);
  }

  function sync() {
    syncScheduled = false;
    makeWidgetButtonTabbable();
    configureAnnouncementLinks();
    const previousBogosDialog = bogosDialog;
    configureBogosDialog();
    if (previousBogosDialog !== bogosDialog) bogosWasOpen = false;

    const nextWidgetDialog = document.getElementById(WIDGET_DIALOG_ID);
    if (nextWidgetDialog !== widgetDialog) {
      widgetDialog = nextWidgetDialog;
      widgetWasOpen = false;
    }

    const bogosOpen = isBogosOpen();
    const widgetOpen = isVisible(widgetDialog);
    widgetDialog?.setAttribute("aria-hidden", widgetOpen ? "false" : "true");

    if (bogosOpen && (!bogosWasOpen || activeModal?.dialog === bogosDialog)) {
      const opener =
        lastFocusedOutsideBogos instanceof HTMLElement
          ? lastFocusedOutsideBogos
          : null;

      if (!bogosWasOpen || activeModal?.dialog !== bogosDialog || refreshActiveTrap) {
        activateModal("bogos", bogosDialog, opener);
      }
    }

    if (widgetOpen && (!widgetWasOpen || activeModal?.dialog === widgetDialog)) {
      if (!widgetWasOpen || activeModal?.dialog !== widgetDialog || refreshActiveTrap) {
        activateModal("widget", widgetDialog, getWidgetButton());
      }
    }

    if (
      activeModal?.kind === "bogos" &&
      (!bogosOpen || activeModal.dialog !== bogosDialog)
    ) {
      deactivateModal();
    } else if (
      activeModal?.kind === "widget" &&
      (!widgetOpen || activeModal.dialog !== widgetDialog)
    ) {
      deactivateModal();
    }

    if (!activeModal) {
      if (widgetOpen) {
        activateModal("widget", widgetDialog, getWidgetButton());
      } else if (bogosOpen) {
        activateModal("bogos", bogosDialog, lastFocusedOutsideBogos);
      }
    }

    bogosWasOpen = bogosOpen;
    widgetWasOpen = widgetOpen;
    refreshActiveTrap = false;
  }

  function scheduleSync(refreshTrap = false) {
    refreshActiveTrap ||= refreshTrap;
    if (syncScheduled) return;

    syncScheduled = true;
    window.requestAnimationFrame(sync);
  }

  function start() {
    document.addEventListener("focusin", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (activeModal?.kind === "bogos" && isBogosOpen()) {
        if (!bogosDialog?.contains(target)) {
          // Cart drawer updates can install their own theme focus trap after
          // BOGOS opens. Re-apply the BOGOS trap on the next frame so it wins.
          scheduleSync(true);
        }
        return;
      }

      if (!bogosDialog?.contains(target)) lastFocusedOutsideBogos = target;
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !activeModal) return;

      const closeButton =
        activeModal.kind === "bogos"
          ? activeModal.dialog.querySelector(BOGOS_CLOSE_SELECTOR)
          : activeModal.dialog.querySelector("#accessiblyAppDialogCloseButton");

      if (!(closeButton instanceof HTMLButtonElement)) return;
      event.preventDefault();
      closeButton.click();
    });

    const observer = new MutationObserver((mutations) => {
      let shouldRefreshTrap = false;

      mutations.forEach((mutation) => {
        if (activeModal?.kind === "bogos" && mutation.type === "childList") {
          // BOGOS commonly opens while the cart drawer is still rendering.
          // That render calls trapFocus(cartDrawer), so refresh BOGOS after it.
          shouldRefreshTrap = true;
        }

        if (
          mutation.type === "childList" &&
          activeModal?.dialog &&
          (activeModal.dialog.contains(mutation.target) ||
            Array.from(mutation.removedNodes).some(
              (node) =>
                node === activeModal.dialog ||
                (node instanceof Element && node.contains(activeModal.dialog)),
            ))
        ) {
          shouldRefreshTrap = true;
        }
      });

      scheduleSync(shouldRefreshTrap);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "tabindex"],
    });

    sync();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
